import { useEffect } from "react"
import * as Sentry from "@sentry/react"
import { isRouteErrorResponse, useRouteError } from "react-router"

import ErrorPage from "./ErrorPage"

const shouldLogRouteErrors =
  import.meta.env.DEV ||
  import.meta.env.VITE_SENTRY_DEBUG_ROUTE_ERRORS === "true"

const shouldCaptureRouteErrorResponse = (status: number) => status >= 500

const getRecoverableRouteErrorCopy = (status: number) => {
  switch (status) {
    case 404:
      return {
        title: "Page not found",
        message: "The page you're looking for doesn't exist.",
      }
    case 403:
      return {
        title: "Access denied",
        message: "You do not have permission to view this page.",
      }
    default:
      return {
        title: "Something went wrong",
        message: "Please go back home and try again.",
      }
  }
}

const RouteErrorBoundary = () => {
  const error = useRouteError()

  useEffect(() => {
    if (shouldLogRouteErrors) {
      console.debug("[RouteErrorBoundary] captured route error", error)
    }

    if (isRouteErrorResponse(error)) {
      if (!shouldCaptureRouteErrorResponse(error.status)) {
        return
      }

      Sentry.captureException(
        new Error(`Route error ${error.status}: ${error.statusText}`),
        {
          tags: {
            source: "react-router",
          },
          extra: {
            status: error.status,
            statusText: error.statusText,
            data: error.data,
          },
        }
      )
      return
    }

    Sentry.captureException(error, {
      tags: {
        source: "react-router",
      },
    })
  }, [error])

  if (
    isRouteErrorResponse(error) &&
    !shouldCaptureRouteErrorResponse(error.status)
  ) {
    const copy = getRecoverableRouteErrorCopy(error.status)
    return (
      <ErrorPage
        title={copy.title}
        message={copy.message}
        redirectTo="/"
        redirectReload={false}
      />
    )
  }

  return (
    <ErrorPage
      title="Unexpected error"
      message="Please refresh the page or try again later."
    />
  )
}

export default RouteErrorBoundary
